import * as React from 'react';
import { AutoComplete } from '@/components/ui/auto-complete';
import axiosInstance from '@/lib/axios';
import { composeBusinessCategory } from '@/lib/business-category';
import { useDebounce } from 'use-debounce';
import { Tag } from 'lucide-react';

type CategoryOption = {
  id: string;
  category: string;
  subCategory: string;
  label: string;
};

interface BusinessCategoryPickerProps {
  businessCategory?: string;
  subCategory?: string;
  onSelect: (value: { businessCategory: string; subCategory: string } | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function BusinessCategoryPicker({
  businessCategory,
  subCategory,
  onSelect,
  placeholder = 'Search category...',
  className,
  disabled,
}: BusinessCategoryPickerProps) {
  const selectedLabel =
    businessCategory?.trim() && subCategory?.trim()
      ? composeBusinessCategory(businessCategory, subCategory)
      : '';

  const [search, setSearch] = React.useState(selectedLabel);
  const [debouncedSearch] = useDebounce(search, 300);
  const [options, setOptions] = React.useState<CategoryOption[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setSearch(selectedLabel);
  }, [selectedLabel]);

  React.useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        if (debouncedSearch.trim()) {
          params.set('q', debouncedSearch.trim());
        }
        const response = await axiosInstance.get(`/api/categories?${params.toString()}`);
        setOptions(response.data.categories || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [debouncedSearch]);

  const selectedItem = React.useMemo(() => {
    if (!businessCategory?.trim() || !subCategory?.trim()) return null;

    const match = options.find(
      (item) => item.category === businessCategory && item.subCategory === subCategory
    );
    if (match) return match;

    return {
      id: `selected:${businessCategory}:${subCategory}`,
      category: businessCategory,
      subCategory,
      label: composeBusinessCategory(businessCategory, subCategory),
    };
  }, [options, businessCategory, subCategory]);

  return (
    <AutoComplete
      placeholder={placeholder}
      className={className}
      icon={<Tag className="h-4 w-4" />}
      isLoading={isLoading}
      value={search}
      onInputChange={setSearch}
      selectedItem={selectedItem}
      keyFn={(item) => item.id}
      displayFn={(item) => item.label}
      onSelect={(item) => {
        if (item) {
          onSelect({ businessCategory: item.category, subCategory: item.subCategory });
          setSearch(item.label);
        } else {
          onSelect(null);
          setSearch('');
        }
      }}
      filterFn={() => options}
      itemToString={(item) => (item ? item.label : '')}
      onBlur={() => {
        setSearch(selectedLabel);
      }}
      mode="server"
      disabled={disabled}
    />
  );
}
